import { useElementSize, useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import { Button } from '../Button/index.ts';

interface ClampTextProps {
  children: React.ReactNode;
  lines?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  renderButton?: (arg: { expand: () => void; expanded: boolean }) => React.ReactNode;
}

export function ClampText({ children, lines = 3, renderButton }: ClampTextProps) {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [lineHeight, setLineHeight] = useState<number>(0);
  const [contentHeight, setContentHeight] = useState<number>(0);

  const { ref: sizeRef, height } = useElementSize();
  const elementRef = useRef<HTMLDivElement>(null);
  const ref = useMergedRef(elementRef, sizeRef);

  useEffect(() => {
    if (typeof window !== 'undefined' && elementRef.current) {
      const lh = window.getComputedStyle(elementRef.current).lineHeight;
      setLineHeight(lh ? parseFloat(lh.replace('px', '')) : 0);
      setContentHeight(elementRef.current.scrollHeight);
    }
  }, [height]);

  const expand = useCallback(() => setExpanded(true), []);

  const expandable: boolean = contentHeight > lineHeight * lines;

  return (
    <div>
      <div
        className={clsx(
          'relative',
          !expanded &&
            clsx(
              lines === 1 && 'line-clamp-1',
              lines === 2 && 'line-clamp-2',
              lines === 3 && 'line-clamp-3',
              lines === 4 && 'line-clamp-4',
              lines === 5 && 'line-clamp-5',
              lines === 6 && 'line-clamp-6'
            )
        )}
        ref={ref}
      >
        {children}
      </div>

      {expandable &&
        !expanded &&
        (renderButton && typeof renderButton === 'function' ? (
          renderButton({ expand, expanded })
        ) : (
          <div className="mt-1.5">
            <Button onClick={expand} variant="plain">
              <span className="underline">
                <FormattedMessage {...buttonCommonI18n.readMore} />
              </span>
            </Button>
          </div>
        ))}
    </div>
  );
}
